import { getTheme, Stack } from "@fluentui/react";
import { CommandBar } from "@fluentui/react/lib/CommandBar";
import { Fragment, useCallback, useEffect, useState } from "react";

import { contractsCache } from "./buildMenu/actions/build-contract-status-actions";
import { buildMenu } from "./buildMenu/build-menu";
import { MenuProps } from "./buildMenu/interfaces/build-menu";
import { contentStyles } from "./styles";

import { CopyPaste } from "@/components/CopyPaste";
import { ContractsService } from "@/core/libs/api";
import { useGetProfile } from "@/core/libs/api/react-query/index";
import { useApi } from "@/core/libs/api/react-query/useApi";
import { BackButton } from "@/modules/Contracts/components/BackButton/BackButton";
import {
  ContractBadgeColors,
  ContractFlags,
  TranslatedContractCurrentStep
} from "@/modules/Contracts/enums";
import { Badge } from "@/modules/Contracts/styles";
import { ServicesCategoriesTypesEnum } from "@/modules/Settings/types/ServicesCategories.types";

export function ViewMenu({
  contract,
  approvalFlow,
  nextStep,
  action,
  setIsVisible,
  setIsContractOccurrencesDrawerVisible,
  setAction,
  setActionMessage,
  toggleActionModal,
  contractId,
  isButtonDisabled
}: MenuProps): JSX.Element {
  const { execute } = useApi();
  const theme = getTheme();
  const userProfile = useGetProfile();
  const permissions = userProfile.data?.data?.role.permissions;
  const [contractStatus, setContractStatus] = useState(null);

  useEffect(() => {
    if (contract.serviceCategory.type === ServicesCategoriesTypesEnum.TAG) {
      const contractsThatCannotBeCanceled = async () => {
        const service = ContractsService();
        const { signal } = new AbortController();
        const { data } = await service.getContractsThatCannotBeCanceled({
          signal
        });
        setContractStatus(data);
      };
      contractsThatCannotBeCanceled();
      contractsCache.push(contractStatus);
    }
  }, []);

  const downloadContract = useCallback(
    async (data?: { signed?: boolean }) => {
      const res = await execute({
        url: `contracts/${contractId}/pdf`,
        params: data,
        showAlert: true,
        messages: {
          success: "PDF gerado com sucesso",
          error: "PDF não disponível"
        }
      });

      if (res.data) {
        window.open(res.data, "_blank");
      }
    },
    [contractId, execute]
  );

  return (
    <Fragment>
      <BackButton />
      <Stack
        className={contentStyles.stackContainer}
        tokens={{ childrenGap: 10 }}
        style={{ marginTop: 0 }}
        horizontal
        verticalAlign="center"
      >
        <CopyPaste
          valueToCopy={contract.id.toString()}
          tooltipText="Copiar número do contrato"
        >
          <h2
            className="ms-fontSize-24"
            style={{ margin: 0, textAlign: "center" }}
            data-clarity-unmask
          >
            Contrato #{contract.id}
          </h2>
        </CopyPaste>

        <Stack
          className={contentStyles.badgeContainer}
          horizontal
          wrap
          tokens={{ childrenGap: 10 }}
        >
          <Stack.Item styles={{ root: { marginLeft: 10 } }}>
            <Badge
              bgColor={ContractBadgeColors[contract?.currentStep?.internalName]}
            >
              {nextStep?.friendlyName ??
                TranslatedContractCurrentStep[
                  contract?.currentStep?.internalName
                ]}
            </Badge>
          </Stack.Item>

          {contract?.flag && contract.flag !== ContractFlags.REGULAR && (
            <Stack.Item styles={{ root: { marginLeft: 10 } }}>
              <Badge bgColor={theme.semanticColors.severeWarningIcon}>
                {ContractFlags[contract.flag]}
              </Badge>
            </Stack.Item>
          )}

          {contract?.activationInstallment?.memberShare > 0 && (
            <Stack.Item styles={{ root: { marginLeft: 10 } }}>
              <Badge bgColor={theme.palette.greenLight}>
                Contrato com Integralização de Capital
              </Badge>
            </Stack.Item>
          )}
        </Stack>

        <div className={contentStyles.commandBarContainer}>
          <CommandBar
            items={buildMenu({
              contract,
              approvalFlow,
              permissions,
              nextStep,
              setIsVisible,
              setIsContractOccurrencesDrawerVisible,
              action,
              toggleActionModal,
              setAction,
              setActionMessage,
              contractId,
              downloadContract,
              isButtonDisabled
            })}
            styles={{
              root: {
                padding: 0
              }
            }}
          />
        </div>
      </Stack>
    </Fragment>
  );
}
